import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Divider,
  Typography,
  CircularProgress
} from '@mui/material';
import { Save, Trash } from 'lucide-react';
import { FilterFieldSx, CSS_PRIMARY } from '../../../../constants/style';
import { saveProduct, deleteProduct } from '../../../../apis/Issues/Product';

/* ── Empty form state ─────────────────────────────────────────────────────── */
const emptyForm = {
  productName: '',
  isActive: true,
  productGUID: 0
};

/* ── Validation ───────────────────────────────────────────────────────────── */
const validate = (form) => {
  const errors = {};
  if (!form.productName.trim()) {
    errors.productName = 'Product Name is required.';
  }
  return errors;
};

const AddProduct = ({ productData = null, userAccess = null, onRefresh, onClose }) => {
  const isEditMode = Boolean(productData);

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isDeleteAllowd = isEditMode ? userAccess?.hasDeletePermission : false;
  const isEditAllowed = userAccess?.hasEditPermission || userAccess?.hasAddPermission;

  useEffect(() => {
    if (productData) {
      setForm({
        productName: productData.productName || '',
        isActive: productData.isActive,
        productGUID: productData.productGUID || 0
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setTouched({});
  }, [productData]);

  // ── Field change ───────────────────────────────────────────────────────────
  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    const next = { ...form, [field]: value };
    setForm(next);

    // Live-validate touched fields
    if (touched[field]) {
      const errs = validate(next);
      setErrors((prev) => ({ ...prev, [field]: errs[field] }));
    }
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const errs = validate(form);
    setErrors((prev) => ({ ...prev, [field]: errs[field] }));
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    // Touch all fields to reveal errors
    const allTouched = Object.fromEntries(Object.keys(form).map((k) => [k, true]));
    setTouched(allTouched);

    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    try {
      const payload = {
        isActive: form.isActive,
        productName: form.productName.trim(),
        productGUID: form.productGUID,
        sortOrder: 0
      };

      const successMsg = isEditMode
        ? `Product updated successfully`
        : `Product created successfully`;

      const response = await saveProduct(payload, successMsg, '');
      if (response?.success) {
        onClose?.();
        onRefresh?.();
      }
    } catch (err) {
      console.log('Failed to save product:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const successMsg = `Product deleted successfully`;
      const response = await deleteProduct(productData?.productGUID, successMsg, '');
      if (response && response.success) {
        onClose?.();
        onRefresh?.();
      }
    } catch (err) {
      console.log('Failed to delete global type:', err);
    } finally {
      setDeleting(false);
    }
  };

  // ── Has any visible error ──────────────────────────────────────────────────
  const hasErrors = Object.keys(validate(form)).length > 0;

  return (
    <Box component="form" noValidate autoComplete="off">
      <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 2.5 }}>
        {/* Name */}
        <Box sx={{ flex: '1 1 50%', minWidth: '200px' }}>
          <TextField
            fullWidth
            label="Product"
            value={form.productName}
            onChange={handleChange('productName')}
            onBlur={handleBlur('productName')}
            error={touched.productName && Boolean(errors.productName)}
            helperText={touched.productName && errors.productName}
            size="small"
            sx={FilterFieldSx}
            slotProps={{
              input: {
                readOnly: !isEditAllowed
              }
            }}
          />
        </Box>

        {/* isActive */}
        <Box sx={{ flex: '1 1 50%', minWidth: '200px', display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.isActive}
                onChange={handleChange('isActive')}
                size="small"
                sx={{
                  color: 'primary.main',
                  '&.Mui-checked': { color: CSS_PRIMARY }
                }}
                readOnly={!isEditAllowed}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Active
              </Typography>
            }
          />
        </Box>
      </Box>

      {/* ── Actions ─────────────────────────────────────────────────────── */}
      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          size="small"
          startIcon={
            deleting ? <CircularProgress size={14} color="inherit" /> : <Trash size={15} />
          }
          disabled={deleting}
          onClick={handleDelete}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            minWidth: 100,
            background: '#e05757',
            '&:hover': { background: '#6d6464' },
            boxShadow: '0 4px 12px rgba(46,204,113,0.3)',
            display: !isEditMode || !isDeleteAllowd ? 'none' : 'inline-flex'
          }}
        >
          {deleting ? 'Deleting...' : 'Delete'}
        </Button>

        <Button
          variant="contained"
          size="small"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save size={15} />}
          disabled={saving || (hasErrors && Object.keys(touched).length > 0)}
          onClick={handleSave}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            minWidth: 100,
            background: 'var(--primary, #36c95f)',
            '&:hover': { background: 'var(--primary-dark, #27ae60)' },
            boxShadow: '0 4px 12px rgba(46,204,113,0.3)',
            display: !isEditAllowed ? 'none' : 'inline-flex'
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};

export default AddProduct;
